import React, { useEffect, useState } from "react";
import app from "./firebase.js";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Outlet,
  useLocation,
} from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";

// =======================
// Import des pages (Éducation)
// =======================
import Dashboard from "./pages/Dashboard.js";
import Accueil from "./pages/Accueil.js";
import Cours from "./pages/Cours.js";
import Contact from "./pages/Contact.js";
import Connexion from "./pages/Connexion.js";
import Inscription from "./pages/Inscription.js";
import ConnexionSMS from "./pages/connexionSMS.js";
import AdminSoumissions from "./pages/AdminSoumissions.js";
import Profile from "./pages/Profile.js";

// =======================
// Import pages (Groupe + Consulting)
// =======================
import Groupe from "./pages/Groupe.js";
import ConsultingHome from "./pages/consulting/ConsultingHome.js";
import ConsultingServices from "./pages/consulting/ConsultingServices.js";
import ConsultingAbout from "./pages/consulting/ConsultingAbout.js";
import ConsultingContact from "./pages/consulting/ConsultingContact.js";

// =======================
// Import des composants
// =======================
import ProtectedRoute from "./components/ProtectedRoute.js";
import Chatbot from "./components/Chatbot.js";

// =======================
// Layouts
// =======================

// Layout "Bare" : aucun header, aucun footer (pour Groupe & Consulting)
function BareLayout() {
  return <Outlet />;
}

// Lien de navigation avec état actif + hover
function NavItem({ to, children, isActive }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Link
      to={to}
      style={{
        ...styles.link,
        ...(isHovered ? styles.linkHover : {}),
        ...(isActive ? styles.activeLink : {}),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
    </Link>
  );
}

// Layout "Éducation" : header global + footer + chatbot
function EducationLayout({ user, onLogout }) {
  const location = useLocation();

  return (
    <>
      <header style={styles.header}>
        <div style={styles.logo}>
          <img
            src="https://cdn-icons-png.flaticon.com/512/906/906175.png"
            alt="logo"
            style={styles.logoImg}
          />
          <h2 style={styles.logoTitle}>Groupe HDC</h2>
        </div>

        <nav style={styles.nav}>
          <NavItem to="/" isActive={location.pathname === "/"}>
            Retour à l’accueil du Groupe
          </NavItem>

          <NavItem to="/contact" isActive={location.pathname === "/contact"}>
            Contact
          </NavItem>

          {user && (
  <NavItem
    to="/cours"
    isActive={location.pathname === "/cours"}
  >
    Cours
  </NavItem>
)}

          {user && (
            <NavItem to="/profile" isActive={location.pathname === "/profile"}>
              Mon profil
            </NavItem>
          )}

          {user && (
            <NavItem
              to="/admin"
              isActive={location.pathname.startsWith("/admin")}
            >
              Tableau de bord admin
            </NavItem>
          )}

          {!user ? (
            <NavItem
              to="/connexion"
              isActive={location.pathname === "/connexion"}
            >
              Connexion / Inscription
            </NavItem>
          ) : (
            <button onClick={onLogout} style={styles.logoutBtn}>
              Se déconnecter
            </button>
          )}
        </nav>
      </header>

      <main style={styles.main}>
        <Outlet />
      </main>

      <footer style={styles.footer}>
        © {new Date().getFullYear()} Groupe HDC — Tous droits réservés
      </footer>

      <Chatbot />
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const auth = getAuth(app);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, [auth]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      alert("🚪 Déconnecté avec succès !");
    } catch (error) {
      console.error("Erreur de déconnexion :", error);
    }
  };

  return (
    <Router>
      <Routes>
        {/* =======================
            ROUTES SANS HEADER
            (Groupe hub + Consulting)
        ======================= */}
        <Route element={<BareLayout />}>
          <Route path="/" element={<Groupe />} />

          <Route path="/consulting" element={<Outlet />}>
            <Route index element={<ConsultingHome />} />
            <Route path="services" element={<ConsultingServices />} />
            <Route path="a-propos" element={<ConsultingAbout />} />
            <Route path="contact" element={<ConsultingContact />} />
          </Route>
        </Route>

        {/* =======================
            ROUTES AVEC HEADER
            (Éducation + Auth + Admin)
        ======================= */}
        <Route element={<EducationLayout user={user} onLogout={handleLogout} />}>
          <Route path="/education" element={<Accueil />} />

          <Route path="/connexion" element={<Connexion />} />
          <Route path="/inscription" element={<Inscription />} />
          <Route path="/connexion-sms" element={<ConnexionSMS />} />

          <Route path="/contact" element={<Contact />} />

          <Route
            path="/cours"
            element={
              <ProtectedRoute>
                <Cours />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminSoumissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </Router>
  );
}

// =======================
// Styles inline
// =======================
const styles = {
  header: {
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    boxShadow: "0 8px 30px rgba(15, 23, 42, 0.08)",
    borderBottom: "1px solid rgba(148, 163, 184, 0.18)",
    padding: "14px 32px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "sticky",
    top: 0,
    zIndex: 1000,
    flexWrap: "wrap",
    gap: "16px",
  },

  logo: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: "fit-content",
  },

  logoImg: {
    width: "42px",
    height: "42px",
    borderRadius: "10px",
    boxShadow: "0 4px 12px rgba(79, 70, 229, 0.18)",
  },

  logoTitle: {
    margin: 0,
    color: "#1e293b",
    fontSize: "20px",
    fontWeight: "700",
    letterSpacing: "-0.02em",
  },

  nav: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    alignItems: "center",
  },

  link: {
  textDecoration: "none",
  color: "#2563eb", // ← bleu propre (pro)
  fontWeight: "500",
  fontSize: "14px",
  padding: "10px 14px",
  borderRadius: "10px",
  transition: "all 0.25s ease",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "40px",
},

  linkHover: {
    background: "rgba(79, 70, 229, 0.08)",
    color: "#4f46e5",
    transform: "translateY(-1px)",
  },

  activeLink: {
    color: "#4f46e5",
    fontWeight: "600",
    background: "rgba(17, 57, 233, 0.1)",
    boxShadow: "inset 0 0 0 1px rgba(79, 70, 229, 0.12)",
  },

  logoutBtn: {
    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    color: "#fff",
    border: "none",
    padding: "10px 16px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
    minHeight: "40px",
    boxShadow: "0 8px 20px rgba(220, 38, 38, 0.18)",
    transition: "all 0.25s ease",
  },

  main: {
    padding: "40px 20px",
    textAlign: "center",
    minHeight: "calc(100vh - 160px)",
    background: "#f8fafc",
  },

  footer: {
    background: "#1e293b",
    color: "#fff",
    padding: "18px",
    textAlign: "center",
    marginTop: "0",
    fontSize: "14px",
    fontWeight: "500",
  },
};

export default App;